/**
 * Blatt „Hat der Kunde das Angebot angenommen?" — angestoßen nach dem Planen
 * eines Termins (lib/angebotFrage.ts). Liegt einmal in der Shell.
 *
 * Gefragt wird nur, wenn am Projekt ein Angebot beim Kunden liegt und noch
 * keins angenommen ist (`GET /projekt/{id}/angebote-offen`). „Noch nicht"
 * schweigt für den Rest der Sitzung.
 */
import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPatch } from "@vimaana/ui";

import { ANGEBOT_FRAGE_EVENT, schonVerneint, verneinen } from "../../lib/angebotFrage";
import { errorText, euro, type OffenesAngebot } from "../../lib/invoices";
import "../../styles/konflikt.css";

type Offen = { projektId: string; angebote: OffenesAngebot[] };

export default function AngebotAnnahmeFrage() {
  const qc = useQueryClient();
  const [offen, setOffen] = useState<Offen | null>(null);
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState("");

  useEffect(() => {
    const hoeren = (e: Event) => {
      const projektId = (e as CustomEvent<{ projektId: string }>).detail?.projektId;
      if (!projektId) return;
      apiGet<{ angebote: OffenesAngebot[] }>(`/api/kontor/projekt/${encodeURIComponent(projektId)}/angebote-offen`)
        .then(({ angebote }) => {
          if (angebote.length && !schonVerneint(projektId, angebote.map((a) => a.nummer))) {
            setFehler("");
            setOffen({ projektId, angebote });
          }
        })
        .catch(() => { /* Rückfrage ist Beiwerk — der Termin steht ja schon */ });
    };
    window.addEventListener(ANGEBOT_FRAGE_EVENT, hoeren);
    return () => window.removeEventListener(ANGEBOT_FRAGE_EVENT, hoeren);
  }, []);

  if (!offen) return null;
  const { projektId, angebote } = offen;
  const nein = () => { verneinen(projektId, angebote.map((a) => a.nummer)); setOffen(null); };
  const ja = async (a: OffenesAngebot) => {
    setBusy(true);
    setFehler("");
    try {
      await apiPatch(`/api/kontor/rechnungen/${a.id}`, { status: "angenommen" });
      qc.invalidateQueries({ queryKey: ["invoices"] });
      qc.invalidateQueries({ queryKey: ["projekt", projektId] });
      qc.invalidateQueries({ queryKey: ["projekte"] });
      setOffen(null);
    } catch (err) {
      setFehler(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  const eins = angebote.length === 1;

  return (
    <div className="kf-schleier" onMouseDown={(e) => { if (e.target === e.currentTarget) nein(); }}>
      <section className="kf-blatt" role="dialog" aria-modal="true" aria-labelledby="af-titel">
        <header className="kf-kopf">
          <h3 id="af-titel">{eins ? `Angebot ${angebote[0].nummer} angenommen?` : "Angebot angenommen?"}</h3>
          <button type="button" className="kf-zu" aria-label="Schließen" onClick={nein}>×</button>
        </header>
        <div className="kf-inhalt">
          <p className="kf-frage">
            Für das Projekt ist jetzt ein Einsatz geplant, aber {eins ? "das Angebot steht" : "die Angebote stehen"} noch
            auf „gestellt". Hat der Kunde zugesagt?
          </p>
          {!eins && angebote.map((a) => (
            <button key={a.id} type="button" className="vg-btn vg-btn-ghost" disabled={busy} onClick={() => ja(a)}>
              {a.nummer} · {euro(a.netto)} netto angenommen
            </button>
          ))}
          {fehler && <p className="kf-hinweis" role="alert">{fehler}</p>}
        </div>
        <footer className="kf-fuss">
          <button type="button" className="vg-btn vg-btn-ghost" disabled={busy} onClick={nein}>Noch nicht</button>
          {eins && (
            <button type="button" className="vg-btn vg-btn-primary" disabled={busy} onClick={() => ja(angebote[0])}>
              {busy ? "Trägt ein …" : "Ja, angenommen"}
            </button>
          )}
        </footer>
      </section>
    </div>
  );
}
